import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, TrendingUp, Users, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  failed: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-slate-100 text-slate-600 border-slate-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

function formatIDR(n) { return "Rp " + (Number(n) || 0).toLocaleString("id-ID"); }

export default function AdminDonations() {
  const [data, setData] = useState({ items: [], total: 0, total_success_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/donations", { params: { status: filter, limit: 200 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const refreshStatus = async (orderId) => {
    setRefreshingId(orderId);
    try {
      const { data } = await api.post(`/donation/refresh/${orderId}`);
      toast.success(`Status: ${data.status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div data-testid="admin-donations-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Donasi</h1>
          <p className="text-slate-600 mt-1">Transaksi Midtrans + status pembayaran.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="card-sf p-6">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider"><Users className="h-4 w-4" /> Total Transaksi</div>
          <div className="mt-2 text-3xl font-bold text-slate-900">{data.total}</div>
        </div>
        <div className="card-sf p-6">
          <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider"><TrendingUp className="h-4 w-4" /> Total Diterima</div>
          <div className="mt-2 text-3xl font-bold text-emerald-600">{formatIDR(data.total_success_amount)}</div>
        </div>
        <div className="card-sf p-6">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter Status</div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="donation-filter">
            <option value="">Semua</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      <div className="card-sf overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Order ID</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Donatur</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Program</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Nominal</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Dibuat</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500"><Loader2 className="h-4 w-4 animate-spin inline-block" /></td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Belum ada donasi.</td></tr>}
            {data.items.map((d) => (
              <tr key={d.order_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 text-xs font-mono font-semibold text-slate-800">{d.order_id}</td>
                <td className="px-5 py-3 text-sm">
                  <div className="font-semibold text-slate-800">{d.is_anonymous ? "Anonim" : d.name}</div>
                  <div className="text-xs text-slate-500">{d.email}</div>
                </td>
                <td className="px-5 py-3 text-xs text-slate-600">{d.program}</td>
                <td className="px-5 py-3 text-right text-sm font-semibold text-slate-900">{formatIDR(d.amount)}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[d.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {d.status}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-slate-500">{(d.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-5 py-3 text-right">
                  <button
                    onClick={() => refreshStatus(d.order_id)}
                    disabled={refreshingId === d.order_id}
                    className="h-8 w-8 rounded-lg border border-slate-200 hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] flex items-center justify-center inline-flex disabled:opacity-40"
                    title="Refresh status dari Midtrans"
                    data-testid={`refresh-${d.order_id}`}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${refreshingId === d.order_id ? "animate-spin" : ""}`} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
